// src/components/ui/Countdown.jsx
import React, { useEffect, useState } from "react";

/**
 * Live countdown to a target Date. Re-renders every second.
 * Returns "—" gracefully if no target is given.
 *
 * Layout: compact horizontal "02 : 14 : 38" with days / hrs / min labels.
 */
export default function Countdown({ target, compact = false, className = "" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!target) {
    return <span className={`text-[var(--txt-3)] ${className}`}>—</span>;
  }

  const ms = Math.max(0, new Date(target).getTime() - now);
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);

  const pad = (n) => String(n).padStart(2, "0");

  if (compact) {
    return (
      <span className={`font-medium ${className}`}>
        {days > 0 ? `${days}d ${pad(hours)}h` : `${pad(hours)}h ${pad(minutes)}m`}
      </span>
    );
  }

  const unit = (n, label) => (
    <div className="text-center">
      <div
        className="font-display font-semibold text-[var(--txt)] leading-none"
        style={{ fontSize: "24px" }}
      >
        {pad(n)}
      </div>
      <div className="text-[10px] tracking-wider uppercase text-[var(--txt-3)] mt-1">
        {label}
      </div>
    </div>
  );

  const sep = (
    <div className="text-[20px] text-[var(--txt-3)] pb-2 leading-none">:</div>
  );

  return (
    <div className={`flex items-end gap-2 ${className}`}>
      {unit(days, "days")}
      {sep}
      {unit(hours, "hrs")}
      {sep}
      {unit(minutes, "min")}
    </div>
  );
}

/**
 * Helper — best-effort "next draw" estimator from a list of weekdays.
 * Mega Millions: Tue & Fri. Powerball: Mon, Wed, Sat.
 * 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
 *
 * NOTE: real draw times (~11pm ET) are approximated. If the backend exposes
 * an exact next-draw time, prefer that over this helper.
 */
export function nextDrawAt(weekdays, hour = 23, minute = 0) {
  const now = new Date();
  for (let i = 0; i < 8; i += 1) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + i);
    candidate.setHours(hour, minute, 0, 0);
    if (weekdays.includes(candidate.getDay()) && candidate.getTime() > now.getTime()) {
      return candidate;
    }
  }
  return null;
}

export const NEXT_MEGA_MILLIONS = () => nextDrawAt([2, 5]); // Tue, Fri
export const NEXT_POWERBALL     = () => nextDrawAt([1, 3, 6]); // Mon, Wed, Sat
